/**
 * Wallet service → flow routing.
 *
 * The POST /api/merchant/gozem_money/home "services" section returns each
 * action as a tile carrying a `link`. This resolver is the SINGLE place that
 * maps a link to the flow it triggers, shared by:
 *   - WalletPage routing        (onAction — which modal to open)
 *   - WalletStore limit selectors (which service's amount limits to project)
 * so the convention can never drift between the two consumers.
 *
 * See GPGT-40760.
 */

/** The wallet flows a service link can resolve to. */
export type WalletServiceAction = 'topup' | 'send' | 'withdraw' | 'qr';

/**
 * Resolves a service `link` to its wallet flow.
 *
 * Matching is by substring because links vary by environment/locale
 * (e.g. "/service/money/topup", "/merchant/money/send"). The token order
 * mirrors the historical routing precedence in WalletPage.onAction.
 *
 * @param link - The service `link` from the home services section
 * @returns The resolved {@link WalletServiceAction}, or `null` when unknown
 */
export function resolveServiceAction(link: string): WalletServiceAction | null {
  const l = (link ?? '').toLowerCase();
  if (l.includes('send') || l.includes('transfer')) return 'send';
  if (l.includes('topup') || l.includes('recharge')) return 'topup';
  if (l.includes('withdraw') || l.includes('retrait')) return 'withdraw';
  if (l.includes('scan') || l.includes('qr')) return 'qr';
  return null;
}
